/**
 * Welcome quick-start: shown on the Dashboard for new users.
 *
 * Two sections:
 *   1. **Onboarding Express** — one-click import of customers + sellers +
 *      employees from a single Excel workbook (saves hours on day 1).
 *   2. **Guía según tu plan** — collapsible, tier-aware checklist with the
 *      recommended workflow for Basic / Premium / Pro.
 *
 * The whole card hides itself if the user has dismissed the dashboard
 * onboarding (so we don't duplicate the "Welcome!" CTA).
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Upload, ChevronDown, BookOpen, Sparkles, Loader2, Crown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { api } from "@/App";
import { useSubscription, tierLabel } from "@/lib/subscription";

// ---------------------------------------------------------------------------
// Tier-specific quick-start playbook. Each step is a small set of links so
// the user can land directly on the page (no scavenger hunt across the app).
// ---------------------------------------------------------------------------
const TIER_GUIDES = {
  basic: {
    headline: "Empezá en 4 pasos · Plan Básico",
    color: "slate",
    description:
      "Tu plan Básico es ideal para arrancar: cargá tus clientes y productos, llevá inventario simple y compartí tu catálogo por WhatsApp.",
    steps: [
      {
        title: "Cargá tu cartera de clientes",
        body: "Importá un CSV/Excel o creá uno por uno. Sin límite de clientes en tu plan.",
        links: [{ label: "Ir a Clientes", to: "/customers" }, { label: "Importar Excel", to: "/import" }],
      },
      {
        title: "Subí tu catálogo de productos",
        body: "Agregá descripción, código y precio. Activá foto + descripción si querés compartir el catálogo.",
        links: [{ label: "Ir a Productos", to: "/products" }],
      },
      {
        title: "Llevá el inventario (kardex)",
        body: "Registrá entradas y salidas de stock. Ya está incluido en tu plan Básico.",
        links: [{ label: "Ir a Inventario", to: "/inventory" }],
      },
      {
        title: "Tomá tu primer pedido",
        body: "Manual o con la pantalla POS. Compartí pedidos por WhatsApp.",
        links: [{ label: "Ir a Pedidos", to: "/orders" }],
      },
    ],
    upsell: {
      title: "¿Listo para más?",
      body: "Pasá a Premium para facturar, llevar cuentas por cobrar, costear productos y compartir tu catálogo público con tus clientes.",
      cta: "Ver planes",
    },
  },
  premium: {
    headline: "Sacale provecho a tu Premium · 6 pasos",
    color: "blue",
    description:
      "Con Premium ya podés facturar, controlar cuentas por cobrar y costos. Ideal para negocios que crecen.",
    steps: [
      {
        title: "Configurá tu empresa y CEDI",
        body: "Datos fiscales, dirección del CEDI y coordenadas para que las rutas optimicen bien.",
        links: [{ label: "Mi Empresa", to: "/my-company" }],
      },
      {
        title: "Importá clientes + productos + vendedores",
        body: "Usá la planilla Onboarding Express (abajo) o el asistente IA de importación.",
        links: [{ label: "Importar con IA", to: "/import" }],
      },
      {
        title: "Costeá tus productos",
        body: "Margenes reales por unidad. Ingredientes, packaging, mano de obra.",
        links: [{ label: "Costeo", to: "/costing" }],
      },
      {
        title: "Emití tus primeras facturas",
        body: "Convertí pedidos en facturas con un click y descontá inventario.",
        links: [{ label: "Facturas", to: "/invoices" }],
      },
      {
        title: "Cobrá lo pendiente (CxC)",
        body: "Recordatorios por WhatsApp para clientes con saldo. Es la función que más plata recupera.",
        links: [{ label: "Cuentas por Cobrar", to: "/receivables" }],
      },
      {
        title: "Compartí tu catálogo público",
        body: "Generá un link único para tus clientes (incluye marca 'Powered by MiNegocio').",
        links: [{ label: "Productos · catálogo", to: "/products" }],
      },
    ],
    upsell: {
      title: "Querés todavía más",
      body: "Pro te da pedidos por foto con IA, recordatorios automáticos, optimización avanzada de rutas, HR y catálogo sin marca.",
      cta: "Comparar con Pro",
    },
  },
  pro: {
    headline: "Estás en el plan completo · 7 pasos",
    color: "amber",
    description:
      "Con Pro tenés todo: IA para pedidos por foto, recordatorios automáticos, rutas optimizadas, HR, tareas recurrentes y catálogo sin marca.",
    steps: [
      {
        title: "Configurá empresa, CEDI y vendedores",
        body: "Coordenadas exactas + vendedores asignados por zona.",
        links: [{ label: "Mi Empresa", to: "/my-company" }],
      },
      {
        title: "Importá toda tu data inicial",
        body: "Onboarding Express trae clientes + vendedores + empleados en un solo paso.",
        links: [{ label: "Import IA", to: "/import" }],
      },
      {
        title: "Activá IA · Pedido desde foto",
        body: "Subí una foto de la lista escrita por tu cliente y la app la convierte en pedido.",
        links: [{ label: "Crear pedido (foto)", to: "/orders" }],
      },
      {
        title: "Optimizá tus rutas",
        body: "El planificador calcula la ruta más corta entre todas las paradas, con ETA por cliente.",
        links: [{ label: "Rutas", to: "/route-planning" }],
      },
      {
        title: "Activá Insights + Pronósticos",
        body: "Top clientes, productos dormidos, ranking de vendedores y proyección a 7-90 días.",
        links: [{ label: "Insights", to: "/insights" }],
      },
      {
        title: "Configurá Recursos Humanos",
        body: "Vacaciones, ausencias y nómina con cálculo de CCSS de Costa Rica.",
        links: [{ label: "RR.HH.", to: "/hr" }],
      },
      {
        title: "Programá tareas recurrentes",
        body: "Cobros mensuales, revisiones de inventario, cumpleaños de clientes.",
        links: [{ label: "Tareas", to: "/tasks" }],
      },
    ],
    upsell: null,
  },
};

export default function WelcomeQuickStart({ onOpenWizard }) {
  const { tier } = useSubscription();
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);
  const fileInputRef = useRef(null);

  // Note: this card lives on the admin Dashboard route, which only admin
  // users reach (sellers have a different /seller/* dashboard). No need
  // to gate by isAdmin (which may briefly be false while context loads).

  const guide = TIER_GUIDES[tier] || TIER_GUIDES.basic;

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/onboarding/template/excel`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error("Failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plantilla_onboarding_minegocio.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada — completala y subila acá.");
    } catch {
      toast.error("Error al descargar plantilla");
    } finally {
      setDownloading(false);
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/onboarding/import-all", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const {
        employees_imported = 0,
        customers_imported = 0,
        sellers_created = [],
        errors = [],
      } = res.data || {};
      const bits = [];
      if (employees_imported) bits.push(`${employees_imported} colaborador${employees_imported === 1 ? "" : "es"}`);
      if (customers_imported) bits.push(`${customers_imported} cliente${customers_imported === 1 ? "" : "s"}`);
      if (sellers_created.length) bits.push(`${sellers_created.length} vendedor${sellers_created.length === 1 ? "" : "es"}`);
      if (bits.length) {
        toast.success("Importados: " + bits.join(", "));
        // Reload dashboard so the 3-step tracker reflects new data
        window.location.reload();
      } else {
        toast.error(errors[0] || "No se importó nada. Revisá la plantilla.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en el onboarding");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card
      data-testid="welcome-quickstart"
      className="mb-6 border border-slate-200 rounded-sm overflow-hidden bg-white"
    >
      <div className="p-5 sm:p-6 bg-gradient-to-br from-[#0B1F3A] to-[#152C57] text-white">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-sm bg-white/10">
            <Sparkles className="h-5 w-5 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Onboarding Express
            </h3>
            <p className="text-sm text-white/80 mt-1 max-w-2xl">
              Descargá la planilla, llenala con tus clientes / vendedores / colaboradores y subila acá. En 2 minutos tu cuenta queda lista.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={downloadTemplate}
            disabled={downloading}
            data-testid="welcome-download-template"
            className="bg-white text-[#0B1F3A] hover:bg-amber-300 hover:text-[#0B1F3A] rounded-sm font-semibold"
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Descargar planilla
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={onFileChosen}
            data-testid="welcome-upload-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="welcome-upload-button"
            className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white rounded-sm"
          >
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Subir planilla completada
          </Button>
          <Link
            to="/import"
            data-testid="welcome-import-ai-link"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm text-white/90 hover:text-amber-300 transition-colors"
          >
            o usá el importador con IA →
          </Link>
          {onOpenWizard && (
            <button
              type="button"
              onClick={onOpenWizard}
              data-testid="welcome-open-wizard"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm text-amber-300 hover:text-white transition-colors"
            >
              · o seguí el asistente paso a paso →
            </button>
          )}
        </div>
      </div>

      <Collapsible open={guideOpen} onOpenChange={setGuideOpen}>
        <CollapsibleTrigger
          asChild
          data-testid="welcome-guide-toggle"
        >
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 hover:bg-slate-50 transition-colors text-left"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <BookOpen className="h-4 w-4 text-[#0B1F3A] shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  Guía rápida según tu plan
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold uppercase ${
                    tier === "pro" ? "bg-amber-100 text-amber-800" :
                    tier === "premium" ? "bg-blue-100 text-blue-800" :
                    "bg-slate-100 text-slate-700"
                  }`}>
                    {tierLabel(tier)}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate">{guide.headline}</div>
              </div>
            </div>
            <ChevronDown
              className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${guideOpen ? "rotate-180" : ""}`}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 sm:px-6 py-4 border-t border-slate-100 bg-slate-50/50">
            <p className="text-sm text-slate-600 mb-4 max-w-3xl">{guide.description}</p>
            <ol className="space-y-3">
              {guide.steps.map((s, i) => (
                <li
                  key={i}
                  data-testid={`welcome-step-${i + 1}`}
                  className="flex gap-3 bg-white border border-slate-200 rounded-sm p-3 sm:p-4"
                >
                  <div className="h-7 w-7 shrink-0 grid place-items-center rounded-sm bg-[#0B1F3A] text-amber-300 text-sm font-bold">
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5">{s.body}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {s.links.map((l, j) => (
                        <Link
                          key={j}
                          to={l.to}
                          data-testid={`welcome-step-${i + 1}-link-${j}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#0B1F3A] hover:text-[#152C57] underline underline-offset-2"
                        >
                          {l.label} →
                        </Link>
                      ))}
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            {guide.upsell && (
              <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-amber-50 border border-amber-200 rounded-sm p-4">
                <Crown className="h-5 w-5 text-amber-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-900">{guide.upsell.title}</p>
                  <p className="text-xs text-amber-800 mt-0.5">{guide.upsell.body}</p>
                </div>
                <Link
                  to="/upgrade"
                  data-testid="welcome-upsell-link"
                  className="text-xs font-bold text-amber-900 hover:text-amber-700 px-3 py-1.5 rounded-sm border border-amber-300 bg-white hover:bg-amber-100 transition-colors whitespace-nowrap"
                >
                  {guide.upsell.cta} →
                </Link>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
